import React from 'react';
import type { CategoryPath, SelectableSearchCategory } from '~/features/catalog/components/navigation/CategoryNavigation';
import { ChildCatalogNavigationItem } from '~/features/catalog/components/navigation/ChildCatalogNavigationItem';
import { CategorySearchResults } from '~/features/catalog/components/navigation/CategorySearchResults';

export interface CategoryItemType {
  selectableCategory: SelectableSearchCategory;
  onCategorySelected: (category: SelectableSearchCategory) => void;
  onShowAllInCategorySelected: (category: SelectableSearchCategory) => void;
  showOnlyRoot: boolean;
  compact?: boolean;
  selectedCategoryPath?: CategoryPath;
  children?: React.ReactNode;
  onSearchCategorySelected?: () => void;
}

export function CatalogNavigationItem({
  selectableCategory,
  onCategorySelected,
  onShowAllInCategorySelected,
  showOnlyRoot,
  compact = false,
  selectedCategoryPath,
  children,
  onSearchCategorySelected
}: Readonly<CategoryItemType>) {
  const hasChildren = selectableCategory.children.length > 0;
  const anyChildSelected = selectableCategory.children.some((x: SelectableSearchCategory) => x.selected);
  const anyChildHasChildren = selectableCategory.children.some((x: SelectableSearchCategory) => x.children.length > 0);
  const isRoot = selectableCategory.item.type === null;
  const isExpandedCategory = hasChildren && selectableCategory.selected;

  if (showOnlyRoot) {
    return (
      <>
        <ChildCatalogNavigationItem
          indent={false}
          onCategorySelected={onCategorySelected}
          onShowAllInCategorySelected={onShowAllInCategorySelected}
          isSelectedBranch={true}
          selectableCategory={selectableCategory}
          compact={compact}
        />
        <CategorySearchResults className={'mt-4'} onSearchCategorySelected={onSearchCategorySelected} />
      </>
    );
  }

  const isSelectedBranch = anyChildSelected && anyChildHasChildren;

  const hasShowAllItem =
    compact &&
    hasChildren &&
    !isRoot &&
    selectedCategoryPath &&
    selectedCategoryPath?.hwgId === selectableCategory.item.path.hwgId &&
    selectedCategoryPath?.hagrId === selectableCategory.item.path.hagrId &&
    selectedCategoryPath?.agrId === selectableCategory.item.path.agrId;

  return (
    <div>
      <ChildCatalogNavigationItem
        indent={!(isExpandedCategory || selectableCategory.item.type === 'hwg')}
        onCategorySelected={onCategorySelected}
        onShowAllInCategorySelected={onShowAllInCategorySelected}
        hasShowAllItem={hasShowAllItem}
        selectableCategory={selectableCategory}
        isSelectedBranch={isSelectedBranch}
        compact={compact}
      />
      {children}
      {!showOnlyRoot &&
        isExpandedCategory &&
        selectableCategory.children
          .filter((x: SelectableSearchCategory) => (anyChildSelected ? x.selected || !anyChildHasChildren : true))
          .map((x: SelectableSearchCategory) => (
            <CatalogNavigationItem
              key={`${x.item.id}-${x.item.name}-item`}
              selectedCategoryPath={selectedCategoryPath}
              selectableCategory={x}
              onCategorySelected={onCategorySelected}
              onShowAllInCategorySelected={onShowAllInCategorySelected}
              showOnlyRoot={showOnlyRoot}
              compact={compact}
            />
          ))}
    </div>
  );
}
